import { computed, ref } from 'vue'
import { format } from 'date-fns'
import type { ServiceSlotsByDate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'
import getDateFnsLocaleByIso from 'src/functions/getDateFnsLocaleByIso'
import getDateItemsFromSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/functions/getDateItemsFromSlots'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import apiServiceSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/api/apiServiceSlots'
import { DAYS_FOR_SERVICE_SLOTS } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/constants'

type UseServiceRowScheduleOptions = {
  lpuId: number
  townTimedelta: number
  serviceId: number
}

/**
 * Слоты услуги в строке цен: запрос, выбранный день, кэш после первого открытия.
 */
export default function useServiceRowSchedule({
  lpuId,
  townTimedelta,
  serviceId,
}: UseServiceRowScheduleOptions) {
  const { context }: CommonContextStore = useContextStore()
  const slots = ref<ServiceSlotsByDate>({})
  const activeDate = ref<string | null>(null)
  const isLoading = ref(false)
  let slotsRequestId = 0

  const dayItems = computed(() => getDateItemsFromSlots(
    slots.value,
    getDateFnsLocaleByIso(context.current_language.iso),
  ))
  const activeDaySlots = computed(() => (
    activeDate.value ? (slots.value[activeDate.value] || []) : []
  ))
  const hasSlots = computed(() => (
    Object.keys(slots.value).some(date => slots.value[date].length > 0)
  ))

  function selectDay(date: string) {
    activeDate.value = date
  }

  async function loadSlots(): Promise<'slots' | 'empty' | 'stale'> {
    if (hasSlots.value) {
      return 'slots'
    }

    slotsRequestId += 1

    const requestId = slotsRequestId

    isLoading.value = true
    slots.value = {}
    activeDate.value = null

    try {
      const { data } = await apiServiceSlots.post({
        days: DAYS_FOR_SERVICE_SLOTS,
        day_start: format(new Date(), 'yyyy-MM-dd'),
        only_free: true,
        town_timedelta: townTimedelta,
        services: [{
          lpu_id: lpuId,
          service_id: serviceId,
        }],
      })

      if (requestId !== slotsRequestId) {
        return 'stale'
      }

      const serviceSlots = (data.result || []).find(item => item.service_id === serviceId)
        || data.result?.[0]
      const nextSlots = serviceSlots?.slots || {}
      const daysWithSlots = Object.keys(nextSlots).filter(day => nextSlots[day].length)

      slots.value = nextSlots

      if (!daysWithSlots.length) {
        return 'empty'
      }

      activeDate.value = dayItems.value.find(item => !item.disabled)?.date
        || daysWithSlots[0]
        || null

      return activeDate.value ? 'slots' : 'empty'
    } catch {
      return requestId === slotsRequestId ? 'empty' : 'stale'
    } finally {
      if (requestId === slotsRequestId) {
        isLoading.value = false
      }
    }
  }

  return {
    dayItems,
    activeDate,
    activeDaySlots,
    isLoading,
    selectDay,
    loadSlots,
  }
}
